import React from 'react';
import { HbrCard, HbrTag } from '@harbor/elements-react-standalone';
import {
  JOURNEY_NODE_HEIGHT,
  JOURNEY_NODE_WIDTH,
} from '../../../support/journeyMap/constants';
import {
  getJourneyCardSubtitle,
  getJourneyCardTitle,
  getJourneyFileTypeLabel,
  getJourneyItemStatusLabel,
  getJourneyProviderTag,
  getJourneyVisualState,
  isShadowingTaskItem,
} from '../../../support/journeyMap/normalizers';
import { JourneyMapRenderableItem } from './journeyMapBoardTypes';

type Props = {
  item: JourneyMapRenderableItem;
  completed?: boolean;
  active?: boolean;
  locked?: boolean;
  notApplicable?: boolean;
  overdue?: boolean;
  pendingValidation?: boolean;
  syncStatus?: string | null;
  onClick?: (item: JourneyMapRenderableItem) => void;
  showStatusTag?: boolean;
  showTypeTag?: boolean;
  typeTagInline?: boolean;
  layoutVariant?: 'default' | 'builder';
};

const formatSyncStatusLabel = (value?: string | null) => {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return '';

  switch (raw) {
    case 'pending':
      return 'Synced Nightly';
    // Launched, but the nightly sync never confirmed it within the grace period.
    case 'stale':
      return 'Not Confirmed';
    case 'validated':
      return 'Validated';
    case 'no_match':
      return 'No Match';
    case 'completed':
      return 'Completed';
    default:
      return raw
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
  }
};

const JourneyMapCard: React.FC<Props> = ({
                                           item,
                                           completed = false,
                                           active = false,
                                           locked = false,
                                           notApplicable = false,
                                           overdue = false,
                                           pendingValidation = false,
                                           syncStatus = null,
                                           onClick,
                                           showStatusTag = false,
                                           showTypeTag = true,
                                           typeTagInline = false,
                                           layoutVariant = 'default',
                                         }) => {
  const isRequired = item?.is_required !== false;
  const visual = getJourneyVisualState({
    completed,
    pendingValidation,
    overdue,
    isRequired,
    notApplicable,
    locked,
  });
  const statusLabel = getJourneyItemStatusLabel({
    completed,
    pendingValidation,
    overdue,
    isRequired,
    notApplicable,
    locked,
  });

  const title = getJourneyCardTitle(item);
  const subtitle = getJourneyCardSubtitle(item);
  const providerTag = getJourneyProviderTag(item);
  const fileTypeLabel = getJourneyFileTypeLabel(item);
  const isDisabled = !!locked || !!notApplicable;
  const normalizedSyncStatus = String(syncStatus || '').trim().toLowerCase();
  const isStalePending = normalizedSyncStatus === 'stale';
  const syncLabel = formatSyncStatusLabel(syncStatus);

  // 'pending' and 'stale' are already carried by the pending-validation tag, so showing the
  // sync label too would say the same thing twice on a card this small.
  const showSyncLabel =
    !!syncLabel &&
    !completed &&
    !(pendingValidation && (normalizedSyncStatus === 'pending' || isStalePending));
  const isShadowing = isShadowingTaskItem(item);
  const hasTaskSubmission = !!item?.task_submission;

  const handleClick = () => {
    if (isDisabled) return;
    onClick?.(item);
  };

  return (
    <div
      aria-disabled={isDisabled}
      data-disabled={isDisabled ? 'true' : 'false'}
      style={{
        width: `${JOURNEY_NODE_WIDTH}px`,
        height: `${JOURNEY_NODE_HEIGHT}px`,
        minWidth: `${JOURNEY_NODE_WIDTH}px`,
        maxWidth: `${JOURNEY_NODE_WIDTH}px`,
        minHeight: `${JOURNEY_NODE_HEIGHT}px`,
        maxHeight: `${JOURNEY_NODE_HEIGHT}px`,
        overflow: 'hidden',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
      }}
    >
      <HbrCard
        selectable={true}
        selected={active && !isDisabled}
        disabled={isDisabled}
        role="button"
        sentiment={visual.sentiment}
        onClick={handleClick}
        style={
          {
            '--width': `${JOURNEY_NODE_WIDTH}px`,
            '--height': `${JOURNEY_NODE_HEIGHT}px`,
            '--min-width': `${JOURNEY_NODE_WIDTH}px`,
            '--max-width': `${JOURNEY_NODE_WIDTH}px`,
            '--min-height': `${JOURNEY_NODE_HEIGHT}px`,
            '--max-height': `${JOURNEY_NODE_HEIGHT}px`,
            '--box-shadow': '--hbr-shadow-xs',
            '--border-radius': '8px',
            '--border-width': '2px',
            '--border-color': visual.borderColor,
            display: 'block',
            overflow: 'hidden',
            opacity: isDisabled ? 0.92 : 1,
          } as React.CSSProperties
        }
      >
        <div
          className="hbr-css__layout-col-2xs"
          style={{
            height: '100%',
            minWidth: 0,
            maxWidth: '100%',
            overflow: 'hidden',
            justifyContent: 'space-between',
          }}
        >
          <div
            className="hbr-css__layout-row-xs hbr-css__layout-justify-between hbr-css__layout-align-start"
            style={{ minWidth: 0, overflow: 'hidden', gap: '8px' }}
          >
            <div
              className="hbr-css__layout-col-3xs"
              style={{
                minWidth: 0,
                flex: 1,
                maxWidth: '100%',
                overflow: 'hidden',
              }}
            >
              <div
                className="hbr-css__text-primary-sm-semibold"
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  textDecoration: notApplicable ? 'line-through' : undefined,
                }}
              >
                {title}
              </div>

              {subtitle ? (
                <div
                  className="hbr-css__text-secondary-sm"
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    minWidth: 0,
                    textDecoration: notApplicable ? 'line-through' : undefined,
                  }}
                >
                  {subtitle}
                </div>
              ) : null}
            </div>

            {/* Provider is the single color-coded chip and always sits in the top-right. */}
            {showTypeTag && providerTag ? (
              <div style={{ flexShrink: 0 }}>
                <HbrTag size="small" accent={providerTag.accent}>
                  {providerTag.label}
                </HbrTag>
              </div>
            ) : null}
          </div>

          {/* Secondary, neutral chips: status + file subtype + validation/sync flags. */}
          <div className="hbr-css__layout-row-xs hbr-css__layout-wrap">
            {showStatusTag ? <HbrTag size="small">{statusLabel}</HbrTag> : null}
            {fileTypeLabel ? <HbrTag size="small">{fileTypeLabel}</HbrTag> : null}
            {pendingValidation ? (
              <HbrTag size="small">
                {isStalePending ? 'Not Confirmed' : 'Pending Validation'}
              </HbrTag>
            ) : null}
            {showSyncLabel ? <HbrTag size="small">{syncLabel}</HbrTag> : null}
            {isShadowing ? <HbrTag size="small">Shadowing</HbrTag> : null}
            {hasTaskSubmission && !completed ? <HbrTag size="small">Submitted</HbrTag> : null}
          </div>
        </div>
      </HbrCard>
    </div>
  );
};

export default JourneyMapCard;